import { useState } from "react";
import { Box } from "@mui/material";
import { COLORS } from "components/v3/Theme/colors";
import { PATHS } from "constants/pathConstants";
import { useHistory } from "react-router-dom";
import SurveyWrapper from "components/v3/sharedComponents/surveys/SurveyWrapper";
import { useDispatch } from "react-redux";
import { setHasActiveCancerDiagnosis } from "store/reducers/user/UserAccountSlice";
import SurveySummary from "components/v3/sharedComponents/surveys/SurveySummary";
import Mixpanel from "utils/mixpanel";
import { Model } from "survey-core";

const SURVEY_ID = "precision-guidance-survey";

const SURVEY_JSON_OVERRIDE = {};

const PrecisionGuidanceSurvey = (): React.ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [summarySurveyModel, setSummarySurveyModel] = useState<Model | null>(
    null
  );

  const handleOnConfirm = (): void => {
    if (!summarySurveyModel) return;

    const everHadCancer = summarySurveyModel.getQuestionByName(
      "have-you-ever-been-told-you-have-cancer"
    );
    const cancerStatus = summarySurveyModel.getQuestionByName(
      "which-cancer-status-best-applies-to-you"
    );
    const cancerType = summarySurveyModel.getQuestionByName("cancer-type");
    const dnaTransplant = summarySurveyModel.getQuestionByName(
      "have-you-had-a-dna-transplant"
    );

    if (everHadCancer?.value === true) {
      if (cancerStatus?.value === "history-of-cancer") {
        Mixpanel.track("Survey completed with history of cancer");
        dispatch(setHasActiveCancerDiagnosis(false));
      } else if (cancerStatus?.value === "active-cancer") {
        Mixpanel.track("Survey completed with active cancer");
        dispatch(setHasActiveCancerDiagnosis(true));
      } else {
        // For "not sure" or no selection, we'll treat it as potentially active
        Mixpanel.track("Survey completed with uncertain cancer status");
        dispatch(setHasActiveCancerDiagnosis(true));
      }
      if (cancerType?.value === "basal-cell-or-squamous-cell-skin-cancer") {
        Mixpanel.track(
          "Survey completed with basal-cell-or-squamous-cell-skin-cancer"
        );
      }
    } else if (dnaTransplant?.value !== "no") {
      Mixpanel.track("Survey completed with DNA transplant");
    } else {
      Mixpanel.track(
        "Survey precision-guidance-survey submitted without blockers from the summary page"
      );
      dispatch(setHasActiveCancerDiagnosis(false));
    }
    setShowSummary(false);
    summarySurveyModel.doComplete();
  };

  const handleOnCompleting = (
    surveyModel: Model,
    options: { allow: boolean }
  ): void => {
    if (!showSummary) {
      Mixpanel.track("Survey summary page shown");
      options.allow = false;
      setSummarySurveyModel(surveyModel);
      setShowSummary(true);
    }
  };

  const handleOnComplete = (): void => {
    history.push(PATHS.ORDER_KIT_SELECTION);
  };

  const handleOnCurrentPageChanging = (surveyModel: Model): void => {
    const everHadCancer = surveyModel.getQuestionByName(
      "have-you-ever-been-told-you-have-cancer"
    );
    const cancerStatus = surveyModel.getQuestionByName(
      "which-cancer-status-best-applies-to-you"
    );
    const dnaTransplant = surveyModel.getQuestionByName(
      "have-you-had-a-dna-transplant"
    );

    if (everHadCancer?.value === true) {
      if (cancerStatus?.value === "active-cancer") {
        Mixpanel.track("Active cancer indicated in survey");
      } else if (cancerStatus?.value === "history-of-cancer") {
        Mixpanel.track("History of cancer indicated in survey");
      }
    }
    if (dnaTransplant?.value && dnaTransplant?.value !== "no") {
      Mixpanel.track("DNA transplant indicated in survey");
    }
  };

  const handleOnSurveyInstantiated = (surveyModel: Model): void => {
    surveyModel.pagePrevText = "Back";
  };

  return (
    <>
      {showSummary && summarySurveyModel && (
        <Box
          sx={{
            backgroundColor: COLORS.WHITE,
            borderRadius: "16px",
            padding: {
              xs: "16px",
              md: "32px 64px 0 64px"
            }
          }}
        >
          <SurveySummary
            onBack={() => {
              Mixpanel.track("Survey summary exited with back button");
              setShowSummary(false);
            }}
            onSubmit={handleOnConfirm}
            surveyModel={summarySurveyModel}
          />
        </Box>
      )}
      <Box
        sx={
          showSummary
            ? {
                position: "absolute",
                left: "-10000px",
                top: "auto",
                width: "1px",
                height: "1px",
                overflow: "hidden"
              }
            : {}
        }
      >
        <SurveyWrapper
          containerClassName="precision-guidance-survey-container"
          onSurveyInstantiated={handleOnSurveyInstantiated}
          onComplete={handleOnComplete}
          onCompleting={handleOnCompleting}
          onCurrentPageChanging={handleOnCurrentPageChanging}
          overrideNextButtonEnabled={true}
          surveyId={SURVEY_ID}
          //surveyJsonOverride={SURVEY_JSON_OVERRIDE}
          showProgressBar={true}
          showStepper={true}
        />
      </Box>
    </>
  );
};

export default PrecisionGuidanceSurvey;
